import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Loader2, CreditCard, CheckCircle, FileText, Building2, Download, ImageOff, Banknote, Phone } from "lucide-react";
import axios from "axios";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import PhotoLightbox from "../components/PhotoLightbox";
import CardFeePayPanel from "../components/CardFeePayPanel";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  sent: "bg-blue-100 text-blue-700",
  partial: "bg-orange-100 text-orange-700",
  paid: "bg-emerald-100 text-emerald-700",
  overdue: "bg-red-100 text-red-700",
};

export default function PublicInvoicePay() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [brokenIds, setBrokenIds] = useState({});
  // Customer-selected payment mode. Cash mode surfaces alt payment
  // instructions (Zelle / Cash App / in-person) and shows the 2%-off
  // cash price. Card mode keeps the standard Stripe checkout flow.
  const [payMode, setPayMode] = useState("card");

  const fetchInvoice = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/invoice/${invoiceId}`);
      setInvoice(res.data);
    } catch (e) {
      setError("Invoice not found or is no longer available.");
    }
    setLoading(false);
  }, [invoiceId]);

  // Dynamic page title — what iMessage/SMS shows as the link preview
  const companyName = invoice?.company?.name || "Tides Team Exteriors";
  const invNum = invoice?.invoice_number;
  const invKind = (invoice?.invoice_type || "invoice").toLowerCase();
  const invLabel = invKind === "deposit" ? "Deposit" : invKind === "balance" ? "Final Invoice" : "Invoice";
  useDocumentTitle(invNum ? `${companyName} ${invLabel} — ${invNum}` : `${companyName} ${invLabel}`);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const handlePay = async (method) => {
    setPaying(true);
    setError("");
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/public/stripe/checkout`, {
        invoice_id: invoiceId,
        origin_url: origin,
        method: typeof method === "string" ? method : undefined,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to start payment. Please try again.");
      setPaying(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
    </div>
  );

  if (error && !invoice) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-slate-700 mb-2">Invoice Not Found</h2>
          <p className="text-sm text-slate-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  const total = invoice.amount || 0;
  const discount = invoice.discount || 0;
  const paid = invoice.paid_amount || 0;
  const remaining = Math.max(total - discount - paid, 0);
  const isPaid = invoice.status === "paid" || remaining <= 0;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 pb-28 sm:pb-8" data-testid="public-invoice-pay">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Company Header */}
        {invoice.company && (
          <div className="text-center">
            <img
              src="/logo-light.png"
              alt={invoice.company.name}
              className="h-16 sm:h-20 w-auto mx-auto mb-2"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            {invoice.company.phone && (
              <p className="text-xs text-slate-400">{invoice.company.phone}</p>
            )}
          </div>
        )}

        {/* Invoice Card */}
        <Card className="border-t-4 border-t-[#0F4C81]" data-testid="invoice-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800" data-testid="invoice-number">
                  {invoice.invoice_number || "Invoice"}
                </h2>
                <p className="text-sm text-slate-500">{invoice.title}</p>
              </div>
              <Badge className={`${STATUS_COLORS[invoice.status] || STATUS_COLORS.pending}`} data-testid="invoice-status">
                {invoice.status?.toUpperCase()}
              </Badge>
            </div>

            {/* Customer Info */}
            {invoice.customer_name && (
              <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-400 mb-0.5">Bill To</p>
                <p className="text-sm font-semibold text-slate-700">{invoice.customer_name}</p>
                {invoice.project_address && (
                  <p className="text-xs text-slate-500">{invoice.project_address}</p>
                )}
              </div>
            )}

            {/* Line Items */}
            {invoice.line_items && invoice.line_items.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Items</p>
                <div className="space-y-1.5">
                  {invoice.line_items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{item.description || item.name}</span>
                      <span className="font-medium text-slate-800">${(item.amount || item.unit_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes (preserves user line breaks) */}
            {invoice.description && (
              <div className="border-t border-slate-100 pt-3 mb-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Notes</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="invoice-notes">{invoice.description}</p>
              </div>
            )}

            {/* Totals */}
            <div className="border-t border-slate-100 pt-3 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span className="text-slate-700">${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Discount</span>
                  <span className="text-emerald-600">-${discount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {paid > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Paid</span>
                  <span className="text-emerald-600">-${paid.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold pt-2 border-t border-slate-100">
                <span className="text-slate-800">Amount Due</span>
                <span className="text-[#0F4C81]" data-testid="amount-due">
                  ${remaining.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {invoice.due_date && (
              <p className="text-xs text-slate-400 mt-3">Due: {invoice.due_date}</p>
            )}
          </CardContent>
        </Card>

        {/* Photos */}
        {invoice.photos?.length > 0 && (
          <Card>
            <CardContent className="p-5">
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" />Photos
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {invoice.photos.map((p, idx) => {
                  const broken = !!brokenIds[p.id];
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => !broken && setLightboxIndex(idx)}
                      disabled={broken}
                      className="text-left rounded-lg overflow-hidden bg-slate-100 group relative block disabled:cursor-default"
                      data-testid={`invoice-photo-${p.id}`}
                    >
                      <div className="aspect-square overflow-hidden flex items-center justify-center">
                        {broken ? (
                          <div className="flex flex-col items-center justify-center text-slate-400 px-2 text-center">
                            <ImageOff className="w-8 h-8 mb-1" />
                            <p className="text-[10px] leading-tight">Image unavailable</p>
                          </div>
                        ) : (
                          <img
                            src={p.url}
                            alt={p.caption || p.original_name || "Photo"}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            loading="lazy"
                            onError={() => setBrokenIds(prev => ({ ...prev, [p.id]: true }))}
                          />
                        )}
                      </div>
                      {p.caption && !broken && (
                        <div className="text-[11px] text-slate-700 bg-white/95 px-2 py-1 truncate" title={p.caption}>
                          {p.caption}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lightbox (swipeable carousel) */}
        <PhotoLightbox
          photos={(invoice.photos || []).filter(p => !brokenIds[p.id])}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />

        {!isPaid && invoice.pending_bank_payment > 0 && (
          <Card className="bg-blue-50 border-blue-200" data-testid="pending-bank-payment">
            <CardContent className="p-4 text-center">
              <p className="text-sm font-bold text-[#0F4C81]">Bank payment in progress</p>
              <p className="text-sm text-slate-600 mt-1">
                A bank payment of ${Number(invoice.pending_bank_payment).toLocaleString('en-US', { minimumFractionDigits: 2 })} is clearing. That takes up to 4 business days. No need to pay again.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Pay Button */}
        {isPaid ? (
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-emerald-700">Paid in Full</h3>
              <p className="text-sm text-emerald-600 mt-1">Thank you for your payment!</p>
            </CardContent>
          </Card>
        ) : (() => {
          const cashAmount = Math.round(remaining * 0.98 * 100) / 100;
          const cashSavings = Math.round((remaining - cashAmount) * 100) / 100;
          const bizPhone = invoice.company?.phone || "843.252.4610";
          const bizPhoneDigits = String(bizPhone).replace(/[^0-9]/g, "");
          if (invoice.card_fee?.enabled) return (<CardFeePayPanel remaining={remaining} pct={invoice.card_fee.pct} bizPhone={bizPhone} bizPhoneDigits={bizPhoneDigits} paying={paying} error={error} onPay={handlePay} />);
          return (
          <div className="space-y-3">
            {/* Payment method toggle — two big tap cards, mobile-first. */}
            <div className="grid grid-cols-2 gap-2" data-testid="pay-mode-selector">
              <button
                type="button"
                onClick={() => setPayMode("card")}
                className={`p-3 rounded-xl border-2 text-left transition-all ${payMode === "card" ? "border-[#0F4C81] bg-[#0F4C81]/5" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                data-testid="pay-mode-card"
              >
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className={`w-4 h-4 ${payMode === "card" ? "text-[#0F4C81]" : "text-slate-400"}`} />
                  <span className={`text-[11px] font-bold uppercase tracking-wider ${payMode === "card" ? "text-[#0F4C81]" : "text-slate-500"}`}>Card / ACH</span>
                </div>
                <div className="text-lg font-bold text-slate-900 tabular-nums">${remaining.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </button>
              <button
                type="button"
                onClick={() => setPayMode("cash")}
                className={`p-3 rounded-xl border-2 text-left transition-all ${payMode === "cash" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                data-testid="pay-mode-cash"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Banknote className={`w-4 h-4 ${payMode === "cash" ? "text-emerald-600" : "text-slate-400"}`} />
                  <span className={`text-[11px] font-bold uppercase tracking-wider ${payMode === "cash" ? "text-emerald-700" : "text-slate-500"}`}>Cash · Save 2%</span>
                </div>
                <div className="text-lg font-bold text-emerald-700 tabular-nums">${cashAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                <div className="text-[10px] text-emerald-600 mt-0.5">Save ${cashSavings.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </button>
            </div>

            {payMode === "card" ? (
              <>
                <Button
                  onClick={handlePay}
                  disabled={paying}
                  className="w-full h-14 bg-[#0F4C81] hover:bg-[#0D4270] text-base font-bold rounded-xl"
                  data-testid="pay-now-btn"
                >
                  {paying ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" />Processing...</>
                  ) : (
                    <><CreditCard className="w-5 h-5 mr-2" />Pay ${remaining.toLocaleString('en-US', { minimumFractionDigits: 2 })} Now</>
                  )}
                </Button>
                <div className="flex flex-col items-center gap-1 text-xs text-slate-500">
                  <span>Secure payment by Stripe</span>
                  <span>Card, bank transfer (ACH), Apple Pay or Google Pay</span>
                </div>
              </>
            ) : (
              // Cash mode — show instructions + call/text CTA. No Stripe
              // charge is attempted; the office confirms cash receipt
              // and marks the invoice paid at the cash price from admin.
              <div
                className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 space-y-3"
                data-testid="cash-pay-panel"
              >
                <div className="flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-bold text-emerald-800">Cash Payment Options</span>
                </div>
                <ul className="text-sm text-slate-700 space-y-1.5 list-disc list-inside">
                  <li><span className="font-semibold">Zelle</span> to {bizPhone}</li>
                  <li><span className="font-semibold">Cash App</span> to {bizPhone}</li>
                  <li><span className="font-semibold">Cash or check</span> handed to the crew on site</li>
                </ul>
                <p className="text-xs text-emerald-800 bg-white/70 rounded-md px-3 py-2 border border-emerald-200">
                  Once we receive payment, your invoice updates to the cash price of <span className="font-bold tabular-nums">${cashAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> and stamps paid. You save <span className="font-bold tabular-nums">${cashSavings.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>.
                </p>
                <a
                  href={`tel:+1${bizPhoneDigits}`}
                  className="flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors"
                  data-testid="cash-pay-call-btn"
                >
                  <Phone className="w-4 h-4" />
                  Call / text {bizPhone}
                </a>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-500 text-center" data-testid="payment-error">{error}</p>
            )}
          </div>
          );
        })()}

        {/* Download PDF (always available) */}
        <a
          href={`${API}/public/invoices/${invoiceId}/pdf`}
          target="_blank"
          rel="noopener noreferrer"
          download={`Invoice-${invoice.invoice_number || invoiceId}.pdf`}
          className="flex items-center justify-center gap-2 w-full h-12 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 transition-colors"
          data-testid="download-pdf-btn"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </a>
      </div>

      {/* Fixed-bottom mobile Pay CTA — always in the thumb zone on phones,
          duplicates the inline Pay button so a customer can pay without
          scrolling. Respects iOS home-indicator safe area. */}
      {!isPaid && remaining > 0 && (
        <div
          className="sm:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur shadow-[0_-4px_16px_rgba(15,76,129,0.08)]"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          data-testid="invoice-mobile-pay-bar"
        >
          <div className="px-4 py-3">
            <Button
              onClick={invoice.card_fee?.enabled ? () => document.querySelector('[data-testid="card-fee-pay-panel"]')?.scrollIntoView({ behavior: "smooth", block: "center" }) : handlePay}
              disabled={paying}
              className="w-full h-12 text-base bg-[#0F4C81] hover:bg-[#0D4270] text-white font-semibold rounded-xl tabular-nums"
              data-testid="invoice-mobile-pay-btn"
            >
              {paying ? (
                <><Loader2 className="w-5 h-5 animate-spin mr-2" />Processing…</>
              ) : (
                <><CreditCard className="w-5 h-5 mr-2" />{invoice.card_fee?.enabled ? "Choose how to pay" : `Pay $${remaining.toLocaleString('en-US', { minimumFractionDigits: 2 })} Now`}</>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
